import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from 'contensis-management-api/lib/models';
import { UserErrorMessages } from '@/helpers/user/ErrorMessage.schema';
import { AuthenticationState } from '@/types/AuthenticationState.types';
import { ClientCredentials } from '@/types/ClientCredentials.types';

const authenticationState: AuthenticationState = {
  hasInitialised: false,
  authenticated: false,
  clientCredentials: null,
  error: false,
  errorMessage: null,
  loading: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState: {
    details: undefined as User | undefined,
    sso: false,
    status: authenticationState,
  },
  reducers: {
    LOGIN_USER(state, { payload }: PayloadAction<{ username: string; password: string } | { sso: true, method?: 'redirect' | 'popup' } | { init: true }>) {
      if (payload && 'sso' in payload) state.sso = payload.sso;
      else state.sso = false;
      state.status.loading = true;
    },
    LOGOUT_USER(state) {
      state.details = undefined;
      state.status = authenticationState;
    },
    SET_HAS_INITIALISED(
      state,
      { payload }: PayloadAction<{ hasInitialised: boolean }>
    ) {
      state.status.hasInitialised = payload.hasInitialised;
    },
    LOGIN_COMPLETE(
      state,
      {
        payload,
      }: PayloadAction<{ user: User; clientCredentials: ClientCredentials }>
    ) {
      state.details = payload.user;
      state.status.authenticated = true;
      state.status.clientCredentials = payload.clientCredentials;
      state.status.error = false;
      state.status.errorMessage = null;
      state.status.loading = false;
    },
    LOGIN_FAILED(state, { payload }: PayloadAction<string>) {
      state.status.authenticated = false;
      state.status.error = true;
      state.status.errorMessage = UserErrorMessages[payload] || payload;
      state.status.loading = false;
    },
  },
});

// Extract the action creators object and the reducer
export const { actions, name, reducer, getInitialState } = userSlice;

// Some TypeScript helper types
export type ActionTypeKeys = keyof typeof actions;

export type ActionType<T extends ActionTypeKeys = ActionTypeKeys> =
  `${typeof name}/${T}`;

export type ActionPayload<T extends ActionTypeKeys = ActionTypeKeys> = Omit<
  ReturnType<typeof actions[T]>,
  'type'
> & { type: ActionType<T> };

export default userSlice;
